import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { PlantSummary } from "../shared/types";
import { fetchPlants } from "./api";

type AfterWaterSave = () => void | Promise<void>;

type AppContextValue = {
  plants: PlantSummary[];
  loading: boolean;
  needLogin: boolean;
  waterSheetPlant: PlantSummary | null;
  openWaterSheet: (plant: PlantSummary, afterSave?: AfterWaterSave) => void;
  closeWaterSheet: () => void;
  onWaterSaved: () => Promise<void>;
  refreshHomePlants: () => Promise<void>;
  setNeedLogin: (v: boolean) => void;
};

const AppContext = createContext<AppContextValue | null>(null);

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}

export function AppProvider({ children }: { children: ReactNode }) {
  const [plants, setPlants] = useState<PlantSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [needLogin, setNeedLogin] = useState(false);
  const [waterSheetPlant, setWaterSheetPlant] = useState<PlantSummary | null>(null);
  const afterWaterSaveRef = useRef<AfterWaterSave | undefined>(undefined);

  const refreshHomePlants = useCallback(async () => {
    try {
      const { plants: list } = await fetchPlants();
      setPlants(list);
      setNeedLogin(false);
    } catch (e) {
      const msg = e instanceof Error ? e.message : "";
      if (msg.includes("Unauthorized")) setNeedLogin(true);
    }
  }, []);

  useEffect(() => {
    void refreshHomePlants().finally(() => setLoading(false));
  }, [refreshHomePlants]);

  const openWaterSheet = useCallback((plant: PlantSummary, afterSave?: AfterWaterSave) => {
    afterWaterSaveRef.current = afterSave;
    setWaterSheetPlant(plant);
  }, []);

  const closeWaterSheet = useCallback(() => {
    setWaterSheetPlant(null);
    afterWaterSaveRef.current = undefined;
  }, []);

  const onWaterSaved = useCallback(async () => {
    const after = afterWaterSaveRef.current;
    closeWaterSheet();
    if (after) await after();
    await refreshHomePlants();
  }, [closeWaterSheet, refreshHomePlants]);

  const value: AppContextValue = {
    plants,
    loading,
    needLogin,
    waterSheetPlant,
    openWaterSheet,
    closeWaterSheet,
    onWaterSaved,
    refreshHomePlants,
    setNeedLogin,
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}
